import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';

import { ActivityLogEmail_ActivityLogFragment, ActivityType } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import logError from 'src/utils/logError';

import ActivityLogBase from '../../Helpers/ActivityLogBase';
import ActivityLogFeedEmail from '../ActivityLogFeedEmail';

interface Fragments {
  activityLog: ActivityLogEmail_ActivityLogFragment;
}

const ActivityLogEmail: FCWithFragments<Fragments> = ({ activityLog }) => {
  const { t } = useTranslation();
  const { details } = activityLog;
  const isInboundCandidateEmail = activityLog.activityType === ActivityType.TypeEmailFromCandidate;

  // eslint-disable-next-line no-underscore-dangle
  if (details?.__typename !== 'ActivityLogEmailDetails') {
    logError('ActivityLogEmail: details is not ActivityLogEmailDetails');
    return null;
  }

  return (
    <ActivityLogBase
      activityLog={activityLog}
      actionLabel={isInboundCandidateEmail ? 'replied via email' : 'emailed the candidate'}
      actionLabelKey={
        isInboundCandidateEmail
          ? 'hiring-dashboard-candidates:candidates.activityLog.repliedViaEmail'
          : 'hiring-dashboard-candidates:candidates.activityLog.emailedTheCandidate'
      }
    >
      <ActivityLogFeedEmail
        emailAddress={isInboundCandidateEmail ? details.fromEmailAddress : details.toEmailAddress}
        emailAddressLabel={
          isInboundCandidateEmail
            ? t('hiring-dashboard-candidates:candidates.activityLog.from', { defaultValue: 'From:' })
            : t('hiring-dashboard-candidates:candidates.activityLog.to', { defaultValue: 'To:' })
        }
        ccAddresses={details.ccAddresses}
        subject={details.subject}
        body={details.body}
      />
    </ActivityLogBase>
  );
};

ActivityLogEmail.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogEmail_activityLog on ActivityLog {
      id
      activityType
      details {
        ... on ActivityLogEmailDetails {
          __typename
          fromEmailAddress
          toEmailAddress
          ccAddresses
          subject
          body
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogEmail;
